/**
 * On-Device Reminders — Performance view (F10 / MEAS-002/003/004; D9/D10).
 * Shows everything `GET /reminders/stats-summary` returns for one reminder (last 7 days):
 * the funnel (scheduled → tapped → converted), delivery per platform with iOS "observed" and
 * "estimated" as two separate labelled numbers (never summed), dismissals, and the
 * not-scheduled / cancelled reasons. Values come through ./stats so zeros read as 0 and fields an
 * older backend leaves out read as "—".
 */
import React, { useEffect, useState } from 'react';
import remindersApi from './api';
import {
  normalizeReminderStats, formatCount, formatPct, formatWindow, funnelSteps,
} from './stats';

const Stat = ({ label, value, hint, testId }) => (
  <div data-testid={testId} style={{ border: '1px solid var(--rm-border)', borderRadius: 'var(--rm-radius-sm)', padding: '12px 14px', minWidth: 0 }}>
    <div style={{ fontSize: 12, color: 'var(--rm-text-muted)', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 600, marginTop: 2 }}>{value}</div>
    {hint && <div style={{ fontSize: 12, color: 'var(--rm-text-muted)', marginTop: 4, lineHeight: '17px' }}>{hint}</div>}
  </div>
);

const NOT_REPORTED = 'Not reported by this server version.';

function ReasonList({ title, block, testId, empty }) {
  return (
    <div data-testid={testId}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span className="rm-flabel" style={{ margin: 0 }}>{title}</span>
        <span style={{ fontSize: 14, fontWeight: 600 }}>{block ? formatCount(block.total) : '—'}</span>
      </div>
      {!block ? <p className="rm-helper">{NOT_REPORTED}</p>
        : block.reasons.length === 0 ? <p className="rm-helper">{empty}</p>
          : (
            <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0, border: '1px solid var(--rm-border)', borderRadius: 'var(--rm-radius-sm)' }}>
              {block.reasons.map((r) => (
                <li key={r.reason} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '8px 12px', borderTop: '1px solid var(--rm-border-soft)', fontSize: 13 }}>
                  <span>{r.label} <span className="rm-mono" style={{ color: 'var(--rm-text-muted)', fontSize: 11 }}>{r.reason}</span></span>
                  <span style={{ fontWeight: 600 }}>{formatCount(r.count)}</span>
                </li>
              ))}
            </ul>
          )}
    </div>
  );
}

export default function ReminderPerformance({ reminderId }) {
  const [state, setState] = useState({ loading: true, error: null, stats: null });

  useEffect(() => {
    let off = false;
    if (!reminderId) { setState({ loading: false, error: null, stats: null }); return undefined; }
    setState({ loading: true, error: null, stats: null });
    remindersApi.statsSummary([reminderId])
      .then((r) => {
        const row = (r.data?.data?.summaries || []).find((x) => x.reminder_id === reminderId);
        if (!off) setState({ loading: false, error: null, stats: normalizeReminderStats(row) });
      })
      .catch((e) => { if (!off) setState({ loading: false, error: e?.response?.data?.message || e.message, stats: null }); });
    return () => { off = true; };
  }, [reminderId]);

  if (!reminderId) return <section className="rm-card"><p className="rm-helper" style={{ margin: 0 }}>Save the reminder to see its performance.</p></section>;
  if (state.loading) return <section className="rm-card"><p className="rm-helper" style={{ margin: 0 }}>Loading performance…</p></section>;
  if (state.error) return <section className="rm-card"><p className="rm-err" style={{ margin: 0 }}>Could not load performance: {state.error}</p></section>;

  const s = state.stats;
  const d = s.delivered;
  const steps = funnelSteps(s);

  return (
    <div data-testid="reminder-performance" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <section className="rm-card">
        <div className="rm-card-h"><h2 className="rm-card-t">Funnel · last 7 days</h2></div>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${steps.length},minmax(0,1fr))`, gap: 12 }}>
          {steps.map((st) => (
            <Stat key={st.key} testId={`perf-${st.key}`} label={st.label} value={formatCount(st.value)}
              hint={st.rateOf ? `${formatPct(st.rate)} ${st.rateOf}` : 'Reminders the phones scheduled'} />
          ))}
        </div>
        {s.conversion ? (
          <p className="rm-helper" data-testid="perf-conversion-note">
            A conversion is a contact who tapped and then did <span className="rm-mono">{s.conversion.goalEvent}</span> within {formatWindow(s.conversion.attributionSeconds)} of the tap. {formatCount(s.conversion.tappers)} contacts tapped.
          </p>
        ) : <p className="rm-helper" data-testid="perf-no-goal">Add a goal in Settings → Goal to measure conversions.</p>}
      </section>

      <section className="rm-card">
        <div className="rm-card-h"><h2 className="rm-card-t">Delivery</h2></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,minmax(0,1fr))', gap: 12 }}>
          <Stat testId="perf-delivered-android" label="Delivered (Android)" value={d ? formatCount(d.android) : '—'} hint={d ? 'Android posted the notification. Exact.' : NOT_REPORTED} />
          <Stat testId="perf-ios-observed" label="Delivered on iOS — observed" value={d ? formatCount(d.iosObserved) : '—'} hint={d ? 'Seen in the foreground or still in the tray at the next open. A lower bound.' : NOT_REPORTED} />
          <Stat testId="perf-ios-estimated" label="Delivered on iOS — estimated" value={d ? formatCount(d.iosEstimated) : '—'} hint={d ? 'Estimate: iOS schedules whose time has passed and were not cancelled. Not measured.' : NOT_REPORTED} />
          <Stat testId="perf-dismissed" label="Dismissed (Android)" value={formatCount(s.dismissed)} hint={s.dismissed == null ? NOT_REPORTED : 'Swiped away. iOS cannot report this.'} />
        </div>
        <p className="rm-helper">iOS does not tell the app when a notification is shown, so iOS delivery is a range: at least the observed number, up to the estimate. The two are separate figures and are not added together.</p>
      </section>

      <section className="rm-card">
        <div className="rm-card-h"><h2 className="rm-card-t">Why some were not shown</h2></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,minmax(0,1fr))', gap: 16 }}>
          <ReasonList testId="perf-schedule-failed" title="Not scheduled — reasons" block={s.scheduleFailed} empty="Every reminder was scheduled." />
          <ReasonList testId="perf-cancelled" title="Cancelled — reasons" block={s.cancelled} empty="None were cancelled." />
        </div>
      </section>
      {s.legacy && <p className="rm-helper" data-testid="perf-legacy">Delivery, dismissals and reasons need a newer server. Scheduled and tapped are shown.</p>}
    </div>
  );
}
